import React, { useEffect, useState } from "react";

interface Book {
    id: number;
    title: string;
    author: string;
    description: string;
    bookNumber: string;
    downloadUrl: string;
    file: {
        url: string;
    };
    image: {
        url: string;
    };
}

const Books: React.FC = () => {
    const [books, setBooks] = useState<Book[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchBooks = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/books?populate=*`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch books");
                }

                const data = await response.json();

                const normalizedBooks: Book[] = (data?.data || []).map((item: any) => ({
                    id: item.id,
                    title: item.title,
                    author: item.author,
                    description: item.description,
                    bookNumber: item.bookNumber,
                    file: item.file,
                    image: item.image,
                }));

                setBooks(normalizedBooks);
            } catch (err) {
                console.error(err);
                setError("Failed to load books.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchBooks();
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center py-20">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-mustardYellow border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-20 text-center text-red-500">
                <p>{error}</p>
            </div>
        );
    }

    if (books.length === 0) {
        return (
            <div className="py-20 text-center text-gray-500">
                <p>No books available at the moment.</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-16">
            <div className="flex flex-col gap-20">
                {books.map((book, index) => (
                    <div
                        key={book.id}
                        className={`flex flex-col items-center gap-10 md:flex-row ${index % 2 !== 0 ? "md:flex-row-reverse" : ""
                            }`}
                    >
                        {/* Book Cover */}
                        <div className="flex w-full justify-center md:w-1/2">
                            <div className="relative aspect-[3/4] w-64 shadow-2xl md:w-80">
                                {book.image.url ? (
                                    <img
                                        src={book.image.url}
                                        alt={book.title}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center bg-gray-200 text-gray-500">
                                        No Cover
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Book Details */}
                        <div className="w-full md:w-1/2">
                            <span className="mb-2 inline-block rounded border border-gray-300 px-2 py-1 text-xs text-gray-500">
                                {book.bookNumber}
                            </span>
                            <h2 className="mb-2 text-3xl font-bold text-darkerGrey">
                                {book.title}
                            </h2>
                            <p className="mb-4 text-sm font-medium text-gray-500">
                                By {book.author}
                            </p>
                            <p className="mb-6 text-gray-600">
                                {book.description}
                            </p>
                            <a
                                href={book.file.url}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className="inline-block rounded-full bg-textBlue px-8 py-3 text-sm font-bold text-darkerGrey transition-colors hover:bg-blue-300"
                            >
                                Download Book
                            </a>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Books;
